import { Input } from '../../../components/Input'
import { PLACEHOLDERS, USERS_PAGE_OPTIONS } from '../../../constants'
import { Table } from '../../../components/Table'
import { columns } from './constants'
import { isTableHasParams } from '../../../helper/common'
import { useDispatch, useSelector } from 'react-redux'
import { getUser } from '../../../redux/store/user/getters'
import { useEffect, useRef, useState } from 'react'
import {
  THealthSystemTableRequestData,
  THealthSystemUsersParams
} from '../../HealthSystemUsers/types'
import { DEFAULT_PAGE } from '../../../components/Table/constants'
import { useParams } from 'react-router-dom'
import axios, { CancelTokenSource } from 'axios'
import { useDebounce } from '../../../hooks/hooks'
import { setLoading } from '../../../redux/store/common/slice'
import { ReactComponent as Close16 } from 'assets/svg/Clos16.svg'
import { ReactComponent as Search16 } from 'assets/svg/Search16.svg'
import { useContractDetailsContext } from '../Providers/ContractDetailsContextProvider'
import {
  TContactUsersFiltersFunc,
  TContractUsersData,
  TContractUsersFilters
} from './types'
import { getContractCoUsers, getContractVendorUsers } from '../api'
import useRouter from '../../../hooks/useRouter'
import { useNavigate } from 'react-router'
import { routes } from '../../../router'
import RadioGroup from '../../../components/RadioGroup/RadioGroup'
import { contractDetailsRoute } from '../ContractDetailsRouter/routes'

const Users = () => {
  const user = useSelector(getUser)
  const dispatch = useDispatch()
  const contractDetailsContext = useContractDetailsContext()
  const {
    actions,
    state: { isAutoGeneratedContract, canSeeHealthSystem }
  } = contractDetailsContext
  const { getContractDetailsAsync } = actions

  const { query } = useRouter()
  const navigate = useNavigate()
  const [activeSubTab, setActiveSubTab] = useState(
    query?.subtab || USERS_PAGE_OPTIONS[0].value
  )

  const isVendorTab = activeSubTab === USERS_PAGE_OPTIONS[1].value

  const [totalNumber, setTotalNumber] = useState(0)
  const [searchValue, setSearchValue] = useState('')
  const [tableData, setTableData] = useState<TContractUsersData[]>([])
  const [sortParams, setSortParams] = useState<THealthSystemTableRequestData>()
  const [filters, setFilters] = useState<TContractUsersFilters>({})
  const [pageInfo, setPageInfo] = useState({ ...DEFAULT_PAGE })

  const { id } = useParams()

  const tableDataTokenRef = useRef<CancelTokenSource>()

  const handleTabs = (e) => {
    dispatch(setLoading(true))
    resetTableData()
    setActiveSubTab(e)
    if (query.type) {
      navigate(
        `${routes.contracts}/${query.type}/${query.id}/${contractDetailsRoute.users}/${e}`
      )
    } else {
      navigate(
        `${routes.contracts}/${query.id}/${contractDetailsRoute.users}/${e}`
      )
    }
  }

  const resetTableData = () => {
    setPageInfo({ ...DEFAULT_PAGE })
    setTableData([])
    setSearchValue('')
    setFilters({})
    setSortParams({})
    setTotalNumber(0)
  }

  const debouncedSearch = useDebounce(searchValue, 500)
  useEffect(() => {
    getTableData({ ...sortParams, ...filters, page: { ...DEFAULT_PAGE } })
    setPageInfo({ ...DEFAULT_PAGE })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, isVendorTab])

  useEffect(() => {
    return () => {
      if (!!tableDataTokenRef.current) {
        tableDataTokenRef.current.cancel()
      }
    }
  }, [])

  const getTableData = ({
    sortField,
    sortOrder,
    filters,
    page
  }: THealthSystemTableRequestData) => {
    dispatch(setLoading(true))
    if (!!tableDataTokenRef.current) {
      tableDataTokenRef.current.cancel()
    }
    const params: THealthSystemUsersParams = { ...filters }
    if (sortField) {
      if (sortOrder) {
        params.ordering = `${sortOrder === 'descend' ? '-' : ''}${sortField}`
      }
      setSortParams({
        sortField,
        sortOrder
      })
    }
    if (searchValue) {
      params.search = searchValue
    }
    const dataPage = page ? page : pageInfo
    params.limit = dataPage.pageSize
    params.offset = (dataPage.pageNumber - 1) * dataPage.pageSize
    tableDataTokenRef.current = axios.CancelToken.source()
    const apiRequest = isVendorTab ? getContractVendorUsers : getContractCoUsers
    apiRequest(id as string, {
      cancelToken: tableDataTokenRef.current?.token,
      params
    })
      .then((resp) => {
        if (!resp.data?.results) {
          return
        }
        setTableData(
          resp.data.results.map((i) => ({
            ...i,
            key: i.uuid
          }))
        )
        setTotalNumber(resp.data.count)
        getContractDetailsAsync()
      })
      .finally(() => dispatch(setLoading(false)))
  }

  const applyFilters: TContactUsersFiltersFunc = (field, appliedFilters) => {
    const newFilters = { ...filters, [field]: appliedFilters }
    setFilters(newFilters)
    getTableData({ ...sortParams, filters: newFilters })
  }

  const handleTableChange = (pagination, _filters, sorter) => {
    const page = {
      pageNumber: pagination.current,
      pageSize: pagination.pageSize
    }
    getTableData({
      sortField: sorter.field,
      sortOrder: sorter.order,
      filters,
      page
    })
    setPageInfo(page)
  }

  const handleChangePageSize = (pageSize) => {
    const page = {
      pageNumber: 1,
      pageSize
    }
    getTableData({
      ...sortParams,
      filters,
      page
    })
    setPageInfo(page)
  }

  return (
    <div className="table-wrapper">
      <div className="mb-24">
        <RadioGroup.Buttons
          options={USERS_PAGE_OPTIONS}
          defaultValue={activeSubTab}
          onChange={handleTabs}
        />
      </div>
      <div className="table-wrapper__header">
        <Input
          className="allow-clear"
          propsInput={{
            allowClear: { clearIcon: <Close16 /> },
            className: 'rfp-hs-users_search',
            placeholder: PLACEHOLDERS.SEARCH,
            prefix: <Search16 />,
            value: searchValue,
            onChange: (e) => setSearchValue(e.target.value)
          }}
        />
      </div>
      <Table<TContractUsersData>
        dataSource={tableData}
        columns={columns({
          applyFilters,
          filters,
          community: user?.community || '',
          isVendorTab,
          isAutoCreated: isAutoGeneratedContract,
          canSeeHealthSystem
        })}
        pageSize={pageInfo.pageSize}
        pagination={{
          pageSize: pageInfo.pageSize,
          current: pageInfo.pageNumber,
          total: totalNumber
        }}
        className="rfp-vendors-data-list-table"
        onChangePage={handleChangePageSize}
        onChange={handleTableChange}
        hasSearchOrFilters={isTableHasParams<TContractUsersFilters>(
          debouncedSearch,
          filters
        )}
      />
    </div>
  )
}

export default Users
